import styled from '@emotion/styled';
import { ErrorRounded, ForkRight } from '@mui/icons-material';
import {
  Box,
  Card,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  tooltipClasses,
  Typography,
} from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeletePopupModal from 'src/components/common-modals/delete-modal';
import {
  appointmentBookFlag,
  isPlannerPastDaysEditable,
  therapyPlanningCaseMixTemplates,
  therapyPlanningTemplate,
} from 'src/config';
import useFeatureToggle, { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { permissions } from 'src/paths';
import { useUpdateTherapyPlanningGate } from 'src/components/therapyPlanning/use-therapy-planning-gate';
import {
  editSingleCellOfTherapyPlanningAction,
  resetCheckSplitMinInPlanner,
  resetSingleCellReducer,
  updateAppointmentEndTimeAction,
  updateAppointmentEndTimeResetAction,
} from 'src/redux/actions/therapyPlanning';
import WarningModal from './commonComponent/warning-modal';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import classes from '../therapyPlanning/input.module.css';
import { DosStatusCard } from './commonComponent/DosStatusCard';
import {
  ActualDischargeDateIcon,
  AnticipateDischargeDateIcon,
  CellEditedIcon,
  PencilIcon,
  TemplatePasteIcon,
} from './commonComponent/therapy-planning-icons';
import TherapyPlanningTemplate_2_0 from './therapy-planning-template-2.0';
import TherapyPlanningTemplateApply from './therapy-planning-template-apply';
import TherapyPlanningTemplateMenu from './therapy-planning-template-menu';
import { useTherapyPlanning } from './therapyPlanningProvider';
import RangeStatusIndicator from './RangeStatusIndicator';
import { PlannerProgressNoteDueTab } from 'src/components/progressNoteDue/ProgressNoteDueIndicators';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';
import { CmgStatus } from 'src/redux/reducers/therapyPlanning/getCmgStatusReducer';
import { neutral } from 'src/theme/colors';
import { confirmCancelTestIds, disciplineCardTestId, planningCellTestId } from './test-ids';

export const LightTooltip = styled(
  ({
    className,
    backgroundColor,
    color,
    ...props
  }: TooltipProps & { backgroundColor?: string; color?: string }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
    />
  )
)(({ theme, backgroundColor, color }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: backgroundColor,
    color,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 12,
  },
}));

export const isTherapyFrameStartDate = (evalDate: any, date: any) => {
  const startDate = moment(evalDate);
  const dateToCheck = moment(date);
  const diffDays = dateToCheck.diff(startDate, 'days');

  return diffDays === 0 || (diffDays % 7 === 0 && diffDays >= 0);
};
const isTherapyFrameEndDate = (evalDate: any, date: any) => {
  if (evalDate === date) return false;

  const startDate = moment(evalDate);
  const dateToCheck = moment(date).clone().add(1, 'day');
  if (dateToCheck.format('YYYY-MM-DD') === startDate.format('YYYY-MM-DD')) return false;
  const diffDays = dateToCheck.diff(startDate, 'days');

  return diffDays === 0 || (diffDays % 7 === 0 && diffDays >= 0);
};

const { THERAPY_TEMPLATE } = permissions;

type TherapyCard_2_0Type = {
  therapyType: 'PT' | 'OT' | 'SLP';
  uncoveredDay: any;
  actualDischargeDate: any;
  therapyPlanningData_2_0: any;
  dischargeDate: any;
  summaryData: any;
  frequencyStartDate: any;
  dosOfTherapyCard: string | null;
  progressNotesDue?: ProgressNoteDue[];
};

const therapyPlanningCardInfo = ['Individual', 'Concurrent', 'Group', '% G&C'];

const TherapyCard_2_0 = (props: TherapyCard_2_0Type) => {
  const {
    therapyType,
    uncoveredDay,
    actualDischargeDate,
    therapyPlanningData_2_0,
    dischargeDate,
    summaryData,
    frequencyStartDate,
    dosOfTherapyCard,
    progressNotesDue,
  } = props;
  const { weekStartDate } = useTherapyPlanning();
  const [therapyTemplatesEnabled] = useFeatureToggle(therapyPlanningTemplate, 'ENTERPRISE');
  const therapyCaseMixTemplatesEnabled = useSelector(
    (state: any) => state?.getPdpmFeatureToggleReducer?.response?.data
  );
  // THIN-7684: weekly CMG range verdict, resolved by the backend per therapy week and stored
  // under the week's '<start>_<end>' key. PT and OT cards read the ptOt verdict, SLP its own.
  const cmgStatusByWeek = useSelector((state: any) => state?.getCmgStatusReducer?.statusByWeek);

  // The recommended range itself is only needed to spell out the tooltip — the verdict comes
  // from the backend. Group minutes arrive as a "min-max" string (e.g. "500-600").
  const cmgResolution = useSelector((state: any) => state?.getCmgResolutionReducer?.response?.data);
  const parseGroupMinutes = (value?: string) => {
    if (!value) return { min: null as number | null, max: null as number | null };
    const [min, max] = value.split('-').map((n) => n?.trim());
    return { min: min ? Number(min) : null, max: max ? Number(max) : null };
  };
  const { min: rangeMin, max: rangeMax } = parseGroupMinutes(
    therapyType === 'SLP' ? cmgResolution?.slpGroupMinutes : cmgResolution?.ptOtGroupMinutes
  );

  // Planned minutes for the therapy week ending on `weekEndDate`, across all three rows
  // (Individual + Concurrent + Group). Only the 9 visible days are loaded, so a week reaching
  // past the window returns null and the tooltip drops the clause rather than under-reporting.
  const getWeeklyPlannedTotal = (weekEndDate?: string): number | null => {
    if (!weekEndDate) return null;
    let total = 0;
    for (let offset = 6; offset >= 0; offset--) {
      const date = moment(weekEndDate).subtract(offset, 'days').format('YYYY-MM-DD');
      const index = therapyPlanningData_2_0?.INDIVIDUAL?.findIndex(
        (cell: any) => cell?.date && moment(cell.date).format('YYYY-MM-DD') === date
      );
      if (!(index >= 0)) return null;
      ['INDIVIDUAL', 'CONCURRENT', 'GROUP'].forEach((row: string) => {
        const value = Number(therapyPlanningData_2_0?.[row]?.[index]?.planMinutes);
        if (!Number.isNaN(value)) total += value;
      });
    }
    return total;
  };

  // A cell closes a therapy week when the NEXT calendar day starts one. Matched on dates rather
  // than array positions, so the indicator survives the week's first or last day scrolling out
  // of the 9-day window.
  const isTherapyWeekEndDate = (date?: string) => {
    if (!date || !frequencyStartDate?.length) return false;
    const nextDate = moment(date).add(1, 'days').format('YYYY-MM-DD');
    return frequencyStartDate?.some(
      (res: any) =>
        res?.frequencyStartDate && moment(res.frequencyStartDate).format('YYYY-MM-DD') === nextDate
    );
  };

  // Verdict for the therapy week ending on `weekEndDate` (the week runs the 6 days before it).
  const getWeekCmgStatus = (weekEndDate?: string): CmgStatus => {
    if (!weekEndDate) return null;
    const weekStart = moment(weekEndDate).subtract(6, 'days').format('YYYY-MM-DD');
    const weekEnd = moment(weekEndDate).format('YYYY-MM-DD');
    const status = cmgStatusByWeek?.[`${weekStart}_${weekEnd}`];
    return (therapyType === 'SLP' ? status?.slpCmgStatus : status?.ptOtCmgStatus) ?? null;
  };
  const getMdsAssessmentRange = useSelector(
    (state: any) => state.getMdsAssessmentRangeReducer?.response?.data?.assessmentsByDate
  );
  // THIN-8150: dates past payer day 100 under a Part A payer, marked by the server only while the
  // tenant's day-100 toggle is on. Empty cells go blank like the days before the episode; cells that
  // already hold minutes keep them on the disabled surface (THIN-8159). Neither is editable.
  const blockedPastDay100 = useSelector((state: any) =>
    (state?.getPayerDayCountReducer?.response?.data ?? [])
      .filter((item: any) => item?.blocked)
      .map((item: any) => item?.date)
      .join(',')
  );
  const isBlockedPastDay100 = (cell: any) =>
    !!cell?.date && blockedPastDay100.split(',').includes(cell.date);
  const isBlankPastDay100 = (cell: any) =>
    isBlockedPastDay100(cell) && !cell?.planMinutes && !cell?.actualMinutes && !cell?.isMissedVisit;

  const isRequiredPlanning = useSelector(
    (state: any) => state.getTherapyPlanningSummaryReducer_2_0?.isRequiredPlanning
  );
  const [copyTemplateStartDate, setCopyTemplateStartDate] = useState(null);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [getTherapyTemplateCell, setTherapyTemplateCell] = useState<any>(null);

  const [applyTemplate, setApplyTemplate] = useState(false);
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');

  const [checkPlannerPastDaysEditable] = useFeatureToggleWhenReady(
    isPlannerPastDaysEditable,
    'FACILITY'
  );

  const handleTemplateCloseApply = () => {
    setTherapyTemplateCell(null);
    setApplyTemplate(false);
  };

  const handleTemplateClose = () => {
    setTherapyTemplateCell(null);
    setOpenTemplate(false);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCopyTemplate = (
    event: React.MouseEvent<HTMLButtonElement>,
    singleCellItem: singleCellType
  ) => {
    event.stopPropagation();
    setTherapyTemplateCell(singleCellItem);
    setAnchorEl(event.currentTarget);
    // setOpenTemplate(true);
  };

  const handleCloseTemplateMenu = () => {
    setTherapyTemplateCell(null);
    setAnchorEl(null);
  };

  function getLastWeekDate(date: any) {
    return moment(date).subtract(1, 'week').add(1, 'day').format('YYYY-MM-DD');
  }

  const isHoverTemplates = (currentDate: any) => {
    if (!copyTemplateStartDate) return false;
    if (
      currentDate >= copyTemplateStartDate &&
      currentDate <= getNextWeekDate(copyTemplateStartDate)
    )
      return true;
    return false;
  };

  function getNextWeekDate(date: any) {
    return moment(date).add(1, 'week').subtract(1, 'day').format('YYYY-MM-DD');
  }

  const dispatch = useDispatch();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const showExpandedPlanner = !!dosOfTherapyCard || selectedPatient?.isPendingEvaluation === true;

  const editSingleCellResponse = useSelector(
    (state: any) => state.editSingleCellOfTherapyPlanningReducer
  );
  const updateAppointmentEndTimeResponse = useSelector(
    (state: any) => state.updateAppointmentEndTimeReducer
  );
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlapPayload, setOverlapPayload] = useState<{
    taskUuid: string;
    time: number | string;
  } | null>(null);
  const lastEditCellRef = useRef<{
    taskUuid: string;
    time: number | string;
    refreshContext: {
      patientId: string;
      discipline: string;
      type: string;
      localDate: string;
      weekStartDate: string;
      weekEndDate: string;
    };
  } | null>(null);

  const therapyPlanning_2_0 = useSelector((state: any) => state.getTherapyPlanningDataReducer_2_0);
  const editabilityByDate: Record<
    string,
    { isConcurrentAllowed: boolean; isGroupAllowed: boolean }
  > = therapyPlanning_2_0?.therapyPlaningFormattedData?.editabilityByDate ?? {};
  const isConcurrentAllowedForDate = (date: string) =>
    editabilityByDate[date]?.isConcurrentAllowed ?? true;
  const isGroupAllowedForDate = (date: string) => editabilityByDate[date]?.isGroupAllowed ?? true;
  // Gray the column label only when the flag is off for every day of the visible week
  const visibleDates: string[] = (therapyPlanningData_2_0?.INDIVIDUAL ?? []).map(
    (c: any) => c?.date
  );
  const isConcurrentDisallowedAllWeek =
    visibleDates.length > 0 && visibleDates.every((d) => !isConcurrentAllowedForDate(d));
  const isGroupDisallowedAllWeek =
    visibleDates.length > 0 && visibleDates.every((d) => !isGroupAllowedForDate(d));
  const therapyPlanningSummary = useSelector(
    (state: any) => state.getTherapyPlanningSummaryReducer_2_0
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  // Segment B: therapy-planning edit gate migrated to the instrumented scopeKey path.
  // (The THERAPY_TEMPLATE gate below still reads permissionData — out of scope here.)
  const canUpdateTherapyPlanning = useUpdateTherapyPlanningGate();

  let therapyPlanningEndDate: any = undefined;
  let therapyPlanningStartDate: any = undefined;

  const inputRefs1 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs2 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs3 = useRef<(HTMLInputElement | null)[]>([]);

  const getNextCellItem = (type: 'individual' | 'concurrent' | 'group', nextIndex: number) => {
    const dataMap = new Map<string, any>([
      ['individual', therapyPlanningData_2_0?.INDIVIDUAL],
      ['concurrent', therapyPlanningData_2_0?.CONCURRENT],
      ['group', therapyPlanningData_2_0?.GROUP],
    ]);

    return dataMap.get(type)?.[nextIndex];
  };

  const disableTheCopyPasteIcon = (dataCell: singleCellType) => {
    if (dataCell?.date >= (actualDischargeDate ?? dischargeDate)) return false;
    return true;
  };

  const getCurrentItemData = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown',
    index: number
  ) => {
    const therapyMapping = new Map([
      [
        'individual',
        new Map([
          ['ArrowUp', null],
          ['ArrowDown', therapyPlanningData_2_0?.CONCURRENT],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', therapyPlanningData_2_0?.INDIVIDUAL],
          ['ArrowDown', therapyPlanningData_2_0?.GROUP],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', therapyPlanningData_2_0?.CONCURRENT],
          ['ArrowDown', null],
        ]),
      ],
    ]);

    const eventMapping = therapyMapping.get(type)?.get(eventType);
    return eventMapping ? eventMapping[index] : undefined;
  };

  const getNextUpDownType = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown'
  ) => {
    const transitions = new Map<string, Map<string, string | undefined>>([
      [
        'individual',
        new Map([
          ['ArrowUp', undefined],
          ['ArrowDown', 'concurrent'],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', 'individual'],
          ['ArrowDown', 'group'],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', 'concurrent'],
          ['ArrowDown', undefined],
        ]),
      ],
    ]);

    return transitions.get(type)?.get(eventType);
  };

  const [hoveredType, setHoveredType] = useState<'individual' | 'concurrent' | 'group' | null>(
    null
  );
  const [dateToShowIcon, setDateToShowIcon] = useState('');

  const [cellItemData, setCellItemData] = useState<any>('');
  const [isEditableCell, setIsEditableCell] = useState(false);

  const [editingCell, setEditingCell] = useState<{
    date: string;
    index: number;
    type: 'individual' | 'concurrent' | 'group';
  } | null>(null);

  const [editingPatientUuid, setEditingPatientUuid] = useState<string | null>(null);

  // Clear editing state when patient changes to prevent stale edits
  useEffect(() => {
    setCellItemData('');
    setEditingCell(null);
    setEditingPatientUuid(null);
  }, [selectedPatient?.uuid]);

  // Detect OVERLAP error from edit single cell and open confirmation modal
  useEffect(() => {
    if (
      !editSingleCellResponse?.loading &&
      !editSingleCellResponse?.isCellEdited &&
      editSingleCellResponse?.response?.code === 'OVERLAP'
    ) {
      const taskUuid =
        editSingleCellResponse?.response?.data?.taskUuid ?? lastEditCellRef.current?.taskUuid;
      const time = editSingleCellResponse?.response?.data?.time ?? lastEditCellRef.current?.time;
      if (taskUuid != null && time != null) {
        setOverlapPayload({ taskUuid, time });
        setOverlapModalOpen(true);
      }
    }
  }, [editSingleCellResponse]);

  // THIN-8321: any other refusal (e.g. no active certification on that date) saved nothing, so
  // drop the typed value if its cell is still open and let it show the server's value again.
  // WeekView_2.0 shows the server's message.
  useEffect(() => {
    if (
      editSingleCellResponse?.loading ||
      editSingleCellResponse?.isCellEdited ||
      !editSingleCellResponse?.response ||
      editSingleCellResponse?.response?.code === 'OVERLAP'
    )
      return;
    if (editingCell?.date === lastEditCellRef.current?.refreshContext?.localDate) {
      setCellItemData('');
      setEditingCell(null);
    }
  }, [editSingleCellResponse]);

  // Reset on success of update appointment end time
  useEffect(() => {
    if (updateAppointmentEndTimeResponse?.isSuccess) {
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      dispatch(updateAppointmentEndTimeResetAction());
    }
  }, [updateAppointmentEndTimeResponse?.isSuccess]);

  const handleOverlapCancel = () => {
    setOverlapModalOpen(false);
    setOverlapPayload(null);
    dispatch(resetSingleCellReducer());
  };

  const handleOverlapConfirm = () => {
    if (!overlapPayload) return;
    dispatch(
      updateAppointmentEndTimeAction({
        providerId,
        taskUuid: overlapPayload.taskUuid,
        time: overlapPayload.time,
        refreshContext: lastEditCellRef.current?.refreshContext,
      })
    );
  };

  type singleCellType = {
    actualMinutes: string | number;
    date: string;
    discipline: string;
    disciplineId: string;
    isMissedVisit: boolean;
    planMinutes: string | number;
    therapyType: string;
    uuid: string;
    hasSplit: any;
  };

  const dispatchEditCell = (data: any) => {
    // Use the patient UUID captured when editing started to prevent stale submissions
    const patientId = editingPatientUuid ?? selectedPatient?.uuid;
    if (patientId !== selectedPatient?.uuid) return;

    // Look up the actual taskUuid from the raw therapy planning response by matching
    // date + disciplineId (data?.uuid is the disciplineId, not the taskUuid)
    const rawPlanningData =
      therapyPlanning_2_0?.response?.response?.data ?? therapyPlanning_2_0?.response?.data ?? [];
    const dayEntry = Array.isArray(rawPlanningData)
      ? rawPlanningData.find((d: any) => d?.date === data?.date)
      : null;
    const matchedPlan = dayEntry?.planning?.find(
      (p: any) =>
        p?.disciplineId === data?.uuid &&
        p?.discipline === therapyType &&
        p?.therapyType === data?.therapyType
    );
    const taskUuid = matchedPlan?.taskUuid ?? data?.uuid;

    lastEditCellRef.current = {
      taskUuid,
      time: data?.planMinutes ? data?.planMinutes : 0,
      refreshContext: {
        patientId,
        discipline: therapyType,
        type: data?.therapyType,
        localDate: data?.date,
        weekStartDate: weekStartDate.format('YYYY-MM-DD'),
        weekEndDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
      },
    };
    dispatch(
      editSingleCellOfTherapyPlanningAction({
        patientId,
        providerId: providerId,
        weekStartDate: weekStartDate.format('YYYY-MM-DD'),
        weekEndDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        params: {
          patientId,
          disciplineId: data?.uuid ? data?.uuid : null,
          planMinutes: data?.planMinutes ? data?.planMinutes : 0,
          type: data?.therapyType,
          discipline: therapyType,
          localDate: data?.date,
        },
      })
    );
  };

  const apiCallToSaveSingleCell = (currentCellData: singleCellType) => {
    if (currentCellData?.planMinutes == cellItemData?.planMinutes) return;
    if (currentCellData?.hasSplit) {
      setTempCellItemData(cellItemData);
      setDeleteModalInfo({
        open: true,
        message:
          'This treatment plan currently includes split sessions. Changing the time will remove the split, reset the sessions duration, and unassign it. Do you want to continue?',
        saveLabel: 'Confirm',
        closeLabel: 'Cancel',
        title: 'Remove treatment split',
        deleteType: 'UPDATE_MIN',
        errorType: 'WARNING',
      });
      return;
    }
    dispatchEditCell(cellItemData);
  };

  const handleEditCell = (
    e: any,
    individual: any,
    index: number,
    type: 'individual' | 'concurrent' | 'group'
  ) => {
    if (
      type === 'individual' &&
      disablePlanerByDischarge(individual?.date, actualDischargeDate ?? dischargeDate)
    ) {
      return;
    }
    if (type === 'concurrent' && !isConcurrentAllowedForDate(individual?.date)) return;
    if (type === 'group' && !isGroupAllowedForDate(individual?.date)) return;
    e.stopPropagation();
    setCellItemData(individual);
    setEditingPatientUuid(selectedPatient?.uuid);
    setEditingCell({ date: individual.date, index, type });
  };

  const [tempCellItemData, setTempCellItemData] = useState<any>('');
  const [deleteModalInfo, setDeleteModalInfo] = useState<any>({
    open: false,
    message: '',
    saveLabel: '',
    closeLabel: '',
    title: '',
    deleteType: '',
    errorType: '',
  });

  const handleCloseInfoModal = () => {
    dispatch(resetCheckSplitMinInPlanner());
    setDeleteModalInfo((prevState: any) => ({ ...prevState, open: false }));
    setTimeout(() => {
      setDeleteModalInfo({
        message: '',
        saveLabel: '',
        closeLabel: '',
        title: '',
        deleteType: '',
        uuid: '',
        errorType: '',
      });
    }, 100);
  };
  const handleSaveSplitMin = () => {
    dispatchEditCell(tempCellItemData);
    handleCloseInfoModal();
  };

  const handleBlurCell = (currentCellData: singleCellType) => {
    apiCallToSaveSingleCell(currentCellData);

    setCellItemData('');
    setEditingCell(null);
  };

  const isSummaryCardLoading = (loading: boolean, disciples: any, type: any) => {
    if (loading && disciples === 'ALL') return true;
    if (loading && disciples === type) return true;
    return false;
  };

  const isPlannerDisable = (dataCell: singleCellType) => {
    // return dataCell.date <= '2024-11-29';
    return false;
  };

  const isBeyondPendingEvalWindow = (currentDate: any) => {
    const planningWindowEndDate = selectedPatient?.planningWindowEndDate;
    if (selectedPatient?.isPendingEvaluation === true && planningWindowEndDate) {
      const windowStart = moment(planningWindowEndDate).subtract(6, 'days').format('YYYY-MM-DD');
      return currentDate > planningWindowEndDate || currentDate < windowStart;
    }
    return false;
  };

  const disablePlanerByDischarge = (currentDate: any, dischargeDate: any) => {
    return currentDate > dischargeDate;
  };

  const disablePlanerSummaryByDischarge = (currentDate: any, dischargeDate: any) => {
    return currentDate > dischargeDate;
  };

  const checkIsDosLessSummary = (date: any, DoS: string | null) => {
    if (DoS === null) return true;
    return date < DoS;
  };

  const disablePlannerMin = (currentDate: any, dischargeDate: any) => {
    if (!dischargeDate) return true;
    return currentDate <= dischargeDate;
  };

  const isDisplayTherapyWeek = (dataCell: singleCellType, frequencyStartDate: any) => {
    if (dataCell?.date > (actualDischargeDate ?? dischargeDate)) return false;
    if (!frequencyStartDate?.length) return false;

    return frequencyStartDate?.some((res: any) => {
      return res?.frequencyStartDate === dataCell?.date;
    });
  };

  const isShowFrequencyAlert = (dataCell: singleCellType, frequencyStartDate: any) => {
    if (!frequencyStartDate?.length) return false;

    return frequencyStartDate?.some((res: any) => {
      return res?.frequencyStartDate === dataCell?.date && res?.showFrequencyAlert;
    });
  };

  const hasFrequencyAlert = (data: Array<{ showFrequencyAlert: boolean }>): boolean => {
    if (!data?.length) return false;
    return data?.some((item) => item?.showFrequencyAlert === true);
  };
  const handleChangeInput = (e: any) => {
    if (
      (/^[0-9]*$/.test(e.target.value) &&
        parseInt(e.target.value) >= 0 &&
        parseInt(e.target.value) <= 480) ||
      e.target.value?.length === 0
    )
      setCellItemData({
        ...cellItemData,
        planMinutes: e.target.value,
      });
  };
  const checkIsDosLess = (singleCell: singleCellType, DoS: string | null) => {
    if (DoS === null) return true;
    return singleCell.date < DoS;
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    list: 'list1' | 'list2' | 'list3',
    currentCellData: singleCellType,
    type: any
  ) => {
    const currentRefs = list === 'list1' ? inputRefs1 : list === 'list2' ? inputRefs2 : inputRefs3;
    const otherRefs = list === 'list1' ? inputRefs2 : inputRefs1;
    if (event.key === 'Enter') {
      apiCallToSaveSingleCell(currentCellData);
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();

      const nextIndex: any = event.key === 'ArrowRight' ? index + 1 : index - 1;
      apiCallToSaveSingleCell(currentCellData);
      setCellItemData(getNextCellItem(type, nextIndex));
      setEditingCell({
        date: getNextCellItem(type, nextIndex)?.date,
        index: nextIndex,
        type,
      });
      setIsEditableCell(true);
      if (nextIndex >= 0 && nextIndex < currentRefs.current.length) {
        currentRefs.current[nextIndex]?.focus();
        // handleBlurCell(individual);
      }
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      let targetRefs: React.MutableRefObject<(HTMLInputElement | null)[]> | null = null;

      if (event.key === 'ArrowUp') {
        if (list === 'list2') {
          targetRefs = inputRefs1;
        } else if (list === 'list3') {
          targetRefs = inputRefs2;
        }
      }

      if (event.key === 'ArrowDown') {
        if (list === 'list1') {
          targetRefs = inputRefs2;
        } else if (list === 'list2') {
          targetRefs = inputRefs3;
        }
      }

      if (getNextUpDownType(type, event.key)) {
        //api call
        apiCallToSaveSingleCell(currentCellData);
        setCellItemData(getCurrentItemData(type, event.key, index));
        setEditingCell({
          date: getCurrentItemData(type, event.key, index)?.date,
          index: index,
          type: getNextUpDownType(type, event.key) as any,
        });
        setIsEditableCell(true);
      }

      if (targetRefs) {
        targetRefs.current[index]?.focus();
      }
    }
  };

  const isPastDates = (item: any) => {
    if (checkPlannerPastDaysEditable) return false;
    return item?.date < moment().format('YYYY-MM-DD');
  };
  const renderFrequency = (frequencyStartDate?: { frequencyValue?: string }[]) => {
    const { frequencyValue = 0 } = frequencyStartDate?.[0] ?? {};
    return dosOfTherapyCard ? frequencyValue : null;
  };

  const toDisplayReasonForGrayOutForDis = (individual: any) => {
    const lastDateCurrentFrame =
      therapyPlanningData_2_0?.INDIVIDUAL?.[therapyPlanningData_2_0?.INDIVIDUAL.length - 1];
    return lastDateCurrentFrame?.date === individual?.date;
  };

  const toDisplayReasonForGrayOutForCert = (individual: any, dosOfTherapyCard: any) => {
    const lastDateCurrentFrame =
      therapyPlanningData_2_0?.INDIVIDUAL?.[therapyPlanningData_2_0?.INDIVIDUAL.length - 1]?.date;
    const firstDateCurrentFrame = therapyPlanningData_2_0?.INDIVIDUAL?.[0]?.date;

    let resultDate: string | null = null;
    if (
      dosOfTherapyCard &&
      firstDateCurrentFrame &&
      lastDateCurrentFrame &&
      new Date(dosOfTherapyCard) >= new Date(firstDateCurrentFrame) &&
      new Date(dosOfTherapyCard) <= new Date(lastDateCurrentFrame)
    ) {
      const oneDayBefore = new Date(dosOfTherapyCard);
      oneDayBefore.setDate(oneDayBefore.getDate() - 1);
      resultDate = oneDayBefore.toISOString().split('T')[0];
    }

    return resultDate === individual?.date || lastDateCurrentFrame === individual?.date;
  };

  const getOneDayBeforeMatch = (
    dischargeDate?: string | Date,
    actualDischargeDate?: string | Date,
    individual?: { date?: string }
  ): boolean => {
    const targetDate = actualDischargeDate || dischargeDate;
    if (!targetDate || !individual?.date) return false;

    const oneDayBefore = new Date(targetDate);
    oneDayBefore.setDate(oneDayBefore.getDate() + 1);
    const formattedDate = oneDayBefore.toISOString().split('T')[0];

    return formattedDate === individual.date;
  };

  const isCellHovered = (type: 'individual' | 'concurrent' | 'group', date: string) =>
    hoveredType === type && dateToShowIcon === date && editingCell?.date !== date;

  const getCellBackgroundColor = (cell: any, index: number, includeOutpatient = false) => {
    if (isHoverTemplates(cell?.date)) return '#EBEEFE';
    if (
      (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay) ||
      getMdsAssessmentRange?.[cell?.date]
    )
      return '#F2F4F7';
    if (
      isPlannerDisable(cell) ||
      disablePlanerByDischarge(cell?.date, actualDischargeDate ?? dischargeDate) ||
      checkIsDosLess(cell, dosOfTherapyCard) ||
      isBlockedPastDay100(cell) ||
      !canUpdateTherapyPlanning ||
      (includeOutpatient && selectedPatient?.isOutPatient && isAppointmentBook)
    )
      return 'rgba(56, 66, 80, 0.08)';
    if (cell?.date === moment().format('YYYY-MM-DD')) return '#EBEEFE';
    return 'none';
  };

  const getCellPointerEvents = (
    cell: any,
    includeDischarge = false,
    includeOutpatient = false
  ): 'none' | 'auto' => {
    if (
      isPlannerDisable(cell) ||
      (includeDischarge &&
        disablePlanerByDischarge(cell?.date, actualDischargeDate ?? dischargeDate)) ||
      isPastDates(cell) ||
      checkIsDosLess(cell, dosOfTherapyCard) ||
      isBlockedPastDay100(cell) ||
      !canUpdateTherapyPlanning ||
      (includeOutpatient && selectedPatient?.isOutPatient && isAppointmentBook)
    )
      return 'none';
    return 'auto';
  };

  const renderCellIcon = (cell: any, type: 'individual' | 'concurrent' | 'group') => {
    if (isCellHovered(type, cell?.date))
      return (
        <IconButton size="small">
          <PencilIcon />
        </IconButton>
      );
    if (
      editSingleCellResponse?.isCellEdited &&
      editSingleCellResponse?.editedCellPayload?.params?.startDate === cell?.date &&
      editSingleCellResponse?.editedCellPayload?.therapyType === therapyType &&
      editSingleCellResponse?.editedCellPayload?.groupType === cell?.therapyType
    )
      return (
        <IconButton sx={{ position: 'relative', margin: '-20px' }}>
          <CellEditedIcon />
        </IconButton>
      );
    return null;
  };

  const renderCellMinutes = (
    cell: any,
    type: 'individual' | 'concurrent' | 'group',
    noWrap = false
  ) => (
    <Box sx={{ position: 'relative', ...(noWrap ? { whiteSpace: 'nowrap' } : {}) }}>
      <Typography
        sx={{ fontSize: 15, fontWeight: 500 }}
        component="span"
        data-testid={planningCellTestId(therapyType, cell?.date, type, 'planned')}
      >
        {cell?.planMinutes ? cell?.planMinutes : '--'}
      </Typography>
      &nbsp;|&nbsp;
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: cell?.actualMinutes ? 700 : undefined }}
      >
        {cell?.isMissedVisit ? 'X' : cell?.actualMinutes ? cell?.actualMinutes : '--'}
      </Typography>
    </Box>
  );

  const renderDischargeIcon = (show: boolean, title: string, icon: React.ReactNode) => {
    if (!show) return null;
    return (
      <Tooltip
        placement="bottom"
        title={title}
      >
        <Box
          sx={{
            padding: '8px',
            position: 'absolute',
            left: '4px',
            boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'white',
            justifyContent: 'center',
            width: '32px',
            zIndex: 9,
            height: '32px',
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  };

  const renderEditableInput = (
    cell: any,
    listId: 'list1' | 'list2' | 'list3',
    inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    type: 'individual' | 'concurrent' | 'group',
    index: number
  ) => {
    const inputBg =
      uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay ? '#F2F4F7' : 'white';
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          width: '70px',
          height: '28px',
          borderRadius: '8px',
          border: '0.5px solid #6366F1',
          background: '#FFF',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          value={cellItemData?.planMinutes || ''}
          onBlur={() => handleBlurCell(cell)}
          key={`${listId}-${index}`}
          onKeyDown={(e) => handleKeyDown(e, index, listId, cell, type)}
          ref={(el) => (inputRefs.current[index] = el)}
          onChange={(e) => handleChangeInput(e)}
          style={{
            borderWidth: '0px',
            width: '40%',
            backgroundColor: inputBg,
            height: '20px',
            textAlign: 'center',
            outline: 'none',
            borderRadius: '8px',
            borderLeft: 'none',
          }}
          className={classes.input}
          autoFocus
          data-testid={planningCellTestId(therapyType, cell?.date, type, 'input')}
        />
        <Divider
          orientation="vertical"
          sx={{ color: '#464646', height: '18px', width: '5px', borderColor: '#6366F1' }}
        />
        <input
          type="text"
          disabled
          value={cell?.isMissedVisit ? 'X' : cell?.actualMinutes ? cell?.actualMinutes : 0}
          style={{
            borderWidth: '0px',
            width: '40%',
            backgroundColor: inputBg,
            height: '20px',
            textAlign: 'center',
            outline: 'none',
            borderRadius: '8px',
          }}
          className={classes.input}
        />
      </Box>
    );
  };

  return (
    <>
      <Card
        data-testid={disciplineCardTestId(therapyType)}
        sx={{
          my: 1,
          borderBottom: `4px solid ${
            therapyType === 'OT' ? '#B1B2F8' : therapyType === 'PT' ? '#8A8CF5' : '#6366F1'
          }`,
        }}
      >
        <Stack
          sx={{ height: dosOfTherapyCard || !!dosOfTherapyCard ? '225px' : '40px' }}
          flexDirection="row"
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            flex=".5"
            sx={{
              borderRight: '1px solid #E5E7EB',
              background:
                therapyType === 'OT' ? '#B1B2F8' : therapyType === 'PT' ? '#8A8CF5' : '#6366F1',
              fontSize: '12px',
              fontWeight: '500',
              color: '#111927',
            }}
          >
            <Stack alignItems="center">
              <Typography
                variant="body1"
                sx={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}
              >
                {therapyType}
              </Typography>
            </Stack>
            {renderFrequency(frequencyStartDate) ? (
              <Stack
                sx={{
                  width: '59px',
                  // height: '24px',
                  borderRadius: '6px',
                  border: `0.2px solid ${
                    selectedPatient?.therapyPlanningStatus === 'REQUIRES PLANNING' &&
                    isRequiredPlanning
                      ? '#F79009'
                      : hasFrequencyAlert(frequencyStartDate)
                      ? '#E41000'
                      : '#6366F1'
                  }`,
                  background: 'rgba(193, 194, 255, 0.60)',
                  color: '#FFF',
                  p: '2px',
                  mt: '2px',
                }}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                {selectedPatient?.therapyPlanningStatus === 'REQUIRES PLANNING' &&
                isRequiredPlanning ? (
                  <Tooltip
                    placement="top"
                    title={'Planning required'}
                  >
                    <ErrorRounded
                      sx={{
                        width: '15px',
                        height: '15px',
                        color: '#F79009',
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                ) : hasFrequencyAlert(frequencyStartDate) ? (
                  <LightTooltip
                    placement="top"
                    backgroundColor={'#B42318'}
                    color={'white'}
                    title={'Frequency alert'}
                  >
                    <ErrorRounded
                      sx={{
                        width: '15px',
                        height: '15px',
                        color: '#F71504',
                        cursor: 'pointer',
                      }}
                    />
                  </LightTooltip>
                ) : null}

                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  {renderFrequency(frequencyStartDate)}
                </Typography>
              </Stack>
            ) : null}
          </Stack>

          <Stack flex="9.5">
            <Stack
              sx={{ width: '100%' }}
              flexDirection="row"
              flex={3}
            >
              {dosOfTherapyCard ? (
                <Stack
                  sx={{
                    height: '100%',
                    borderRight: '1px solid #E5E7EB',
                    backgroundColor: '#F8F9FA',
                    maxWidth: '128px',
                    minWidth: '83px',
                  }}
                  flex=".982"
                >
                  {therapyPlanningCardInfo?.map((info: string) => {
                    const isLabelDimmed =
                      (info === 'Concurrent' && isConcurrentDisallowedAllWeek) ||
                      (info === 'Group' && isGroupDisallowedAllWeek);
                    return (
                      <Stack
                        key={info}
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          height: '33.3%',
                          fontWeight: '500',
                          fontSize: '13px',
                          color: isLabelDimmed ? neutral[500] : neutral[900],
                          opacity: isLabelDimmed ? 0.5 : 1,
                          userSelect: 'none',
                        }}
                      >
                        {info}
                      </Stack>
                    );
                  })}
                </Stack>
              ) : null}
              <Stack sx={{ flex: '8.5' }}>
                {!therapyPlanning_2_0?.loading && (
                  <Stack
                    flexDirection="row"
                    sx={{
                      width: '100%',
                      flex: showExpandedPlanner ? 1 : undefined,
                      height: showExpandedPlanner ? undefined : '100%',
                    }}
                  >
                    {therapyPlanningData_2_0?.INDIVIDUAL?.map((individual: any, index: any) => {
                      if (
                        isTherapyFrameEndDate(
                          frequencyStartDate?.[0]?.dateOfService ?? null,
                          individual?.date
                        )
                      )
                        therapyPlanningEndDate = individual?.date;
                      if (
                        isTherapyFrameStartDate(
                          frequencyStartDate?.[0]?.dateOfService ?? null,
                          individual?.date
                        )
                      )
                        therapyPlanningStartDate = individual?.date;

                      // Weekly CMG verdict for the therapy week ENDING on this cell. null when
                      // this is not a week's last day or the backend returned no verdict.
                      const weekCmgStatus =
                        therapyCaseMixTemplatesEnabled && isTherapyWeekEndDate(individual?.date)
                          ? getWeekCmgStatus(individual?.date)
                          : null;

                      return (
                        <LightTooltip
                          placement="top"
                          backgroundColor={''}
                          color={''}
                          title={''}
                          key={index}
                        >
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: `calc(100% / 9)`,
                              position: 'relative',
                              borderLeft: isDisplayTherapyWeek(individual, frequencyStartDate)
                                ? `6px solid ${
                                    isShowFrequencyAlert(individual, frequencyStartDate)
                                      ? '#E41000'
                                      : '#6750A4'
                                  }`
                                : 'none',

                              fontSize: '14px',
                              fontWeight: '400',
                              color: neutral[900],
                              cursor: 'pointer',
                              backgroundColor: getCellBackgroundColor(individual, index),
                              pointerEvents: getCellPointerEvents(individual),
                            }}
                            onMouseEnter={() => {
                              setHoveredType('individual');
                              setDateToShowIcon(individual?.date);
                            }}
                            onMouseLeave={() => {
                              setHoveredType(null);
                              setDateToShowIcon('');
                            }}
                            onClick={(e) => handleEditCell(e, individual, index, 'individual')}
                            data-testid={planningCellTestId(
                              therapyType,
                              individual?.date,
                              'individual'
                            )}
                          >
                            {/* Weekly CMG status, shown on the week's last day just before the
                                next week's blue line. Anchored by date, so it stays put when the
                                week's first or last day scrolls out of the 9-day window. */}
                            {weekCmgStatus && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '2px',
                                  // Inside the last-day cell, left of the next week's blue line.
                                  right: '10px',
                                  zIndex: 3,
                                  lineHeight: 0,
                                }}
                              >
                                <RangeStatusIndicator
                                  status={weekCmgStatus}
                                  label={therapyType === 'SLP' ? 'SLP' : 'PT+OT'}
                                  weekStart={moment(individual?.date)
                                    .subtract(6, 'days')
                                    .format('M/D')}
                                  weekEnd={moment(individual?.date).format('M/D')}
                                  planned={getWeeklyPlannedTotal(individual?.date)}
                                  min={rangeMin}
                                  max={rangeMax}
                                />
                              </Box>
                            )}
                            {/* THIN-8144: each open progress note marks its own due-date cell, on this discipline only (THIN-8192). */}
                            {(() => {
                              const due = progressNotesDue?.find(
                                (note) => note.dueDate === individual?.date
                              );
                              return (
                                due && (
                                  <PlannerProgressNoteDueTab
                                    due={due}
                                    today={moment().format('YYYY-MM-DD')}
                                  />
                                )
                              );
                            })()}
                            {renderDischargeIcon(
                              individual?.date === dischargeDate,
                              'Anticipated Discharge',
                              <AnticipateDischargeDateIcon />
                            )}
                            {renderDischargeIcon(
                              individual?.date === actualDischargeDate,
                              'Discharge',
                              <ActualDischargeDateIcon />
                            )}
                            {disablePlannerMin(
                              individual?.date,
                              actualDischargeDate ?? dischargeDate
                            ) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '-webkit-fill-available',
                                  justifyContent: isCellHovered('individual', individual?.date)
                                    ? 'normal'
                                    : 'center',
                                  gap: 0.1,
                                  height: '100%',
                                }}
                              >
                                {renderCellIcon(individual, 'individual')}

                                {individual?.hasSplit ? (
                                  <Tooltip title="Split Task">
                                    <span>
                                      <ForkRight sx={{ marginRight: '2px', fontSize: '20px' }} />
                                    </span>
                                  </Tooltip>
                                ) : null}
                                {editingCell?.date === individual?.date &&
                                editingCell?.type === 'individual' ? (
                                  renderEditableInput(
                                    individual,
                                    'list1',
                                    inputRefs1,
                                    'individual',
                                    index
                                  )
                                ) : isBlankPastDay100(individual) ? null : checkIsDosLess(
                                    individual,
                                    dosOfTherapyCard
                                  ) ? (
                                  <DosStatusCard
                                    index={index}
                                    showArrow={true}
                                    arrowCondition={false}
                                    labelCondition={toDisplayReasonForGrayOutForCert(
                                      individual,
                                      dosOfTherapyCard
                                    )}
                                    label="No Active Certification"
                                  />
                                ) : (
                                  renderCellMinutes(individual, 'individual')
                                )}
                              </Box>
                            ) : (
                              <DosStatusCard
                                index={index}
                                showArrow={true}
                                arrowCondition={getOneDayBeforeMatch(
                                  dischargeDate,
                                  actualDischargeDate,
                                  individual
                                )}
                                labelCondition={toDisplayReasonForGrayOutForCert(
                                  individual,
                                  dosOfTherapyCard
                                )}
                                label={
                                  actualDischargeDate && actualDischargeDate <= individual?.date
                                    ? 'No Active Certification'
                                    : 'Anticipated Discharge Set'
                                }
                              />
                            )}
                          </Stack>
                        </LightTooltip>
                      );
                    })}
                  </Stack>
                )}

                {dosOfTherapyCard ? (
                  <>
                    {!therapyPlanning_2_0?.loading && (
                      <Stack
                        flexDirection="row"
                        sx={{ width: '100%', flex: 1 }}
                      >
                        {therapyPlanningData_2_0?.CONCURRENT?.map((concurrent: any, index: any) => {
                          const isConcurrentDisabled = !isConcurrentAllowedForDate(
                            concurrent?.date
                          );
                          return (
                            <LightTooltip
                              slotProps={{
                                popper: {
                                  modifiers: [
                                    {
                                      name: 'offset',
                                      options: {
                                        offset: [0, 48],
                                      },
                                    },
                                  ],
                                },
                              }}
                              disableHoverListener={isConcurrentDisabled}
                              disableFocusListener={isConcurrentDisabled}
                              disableTouchListener={isConcurrentDisabled}
                              backgroundColor={''}
                              color={''}
                              placement="top"
                              title={''}
                              key={index}
                            >
                              <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                  width: `calc(100% / 9)`,
                                  fontSize: '14px',
                                  position: 'relative',
                                  fontWeight: '400',
                                  borderLeft: isDisplayTherapyWeek(concurrent, frequencyStartDate)
                                    ? `6px solid ${
                                        isShowFrequencyAlert(concurrent, frequencyStartDate)
                                          ? '#E41000'
                                          : '#6750A4'
                                      }`
                                    : 'none',
                                  color: isConcurrentDisabled ? neutral[500] : neutral[900],
                                  cursor: isConcurrentDisabled ? 'not-allowed' : 'pointer',
                                  backgroundColor: isConcurrentDisabled
                                    ? 'rgba(56, 66, 80, 0.08)'
                                    : getCellBackgroundColor(concurrent, index, true),
                                  pointerEvents: isConcurrentDisabled
                                    ? 'none'
                                    : getCellPointerEvents(concurrent, true, true),
                                }}
                                onMouseEnter={() => {
                                  setHoveredType('concurrent');
                                  setDateToShowIcon(concurrent?.date);
                                }}
                                onMouseLeave={() => {
                                  setHoveredType(null);
                                  setDateToShowIcon('');
                                }}
                                onClick={(e) => {
                                  if (isConcurrentDisabled) return;
                                  handleEditCell(e, concurrent, index, 'concurrent');
                                }}
                                data-testid={planningCellTestId(
                                  therapyType,
                                  concurrent?.date,
                                  'concurrent'
                                )}
                              >
                                {!isConcurrentDisabled &&
                                isDisplayTherapyWeek(concurrent, frequencyStartDate) ? (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: '-4px',

                                      width: '12px',
                                      height: '16px',
                                      clipPath: 'polygon(0% 0%,100% 46%,100% 54%, 0% 100%)',
                                      backgroundColor: isShowFrequencyAlert(
                                        concurrent,
                                        frequencyStartDate
                                      )
                                        ? '#E41000'
                                        : '#6750A4',
                                    }}
                                  />
                                ) : null}
                                {disablePlannerMin(
                                  concurrent?.date,
                                  actualDischargeDate ?? dischargeDate
                                ) ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '-webkit-fill-available',
                                      justifyContent: isCellHovered('concurrent', concurrent?.date)
                                        ? 'normal'
                                        : 'center',
                                      gap: 0.2,
                                    }}
                                  >
                                    {renderCellIcon(concurrent, 'concurrent')}
                                    {editingCell?.date === concurrent?.date &&
                                    editingCell?.type === 'concurrent'
                                      ? renderEditableInput(
                                          concurrent,
                                          'list1',
                                          inputRefs2,
                                          'concurrent',
                                          index
                                        )
                                      : checkIsDosLess(concurrent, dosOfTherapyCard) ||
                                        isBlankPastDay100(concurrent)
                                      ? null
                                      : renderCellMinutes(concurrent, 'concurrent')}
                                  </Box>
                                ) : null}
                              </Stack>
                            </LightTooltip>
                          );
                        })}
                      </Stack>
                    )}

                    {!therapyPlanning_2_0?.loading && (
                      <Stack
                        flexDirection="row"
                        sx={{ width: '100%', flex: 1 }}
                      >
                        {therapyPlanningData_2_0?.GROUP?.map((group: any, index: any) => {
                          if (
                            isTherapyFrameStartDate(
                              frequencyStartDate?.[0]?.dateOfService ?? null,
                              group?.date
                            )
                          )
                            therapyPlanningStartDate = group?.date;

                          const isGroupDisabled = !isGroupAllowedForDate(group?.date);

                          return (
                            <LightTooltip
                              key={index}
                              disableHoverListener={isGroupDisabled}
                              disableFocusListener={isGroupDisabled}
                              disableTouchListener={isGroupDisabled}
                              backgroundColor={''}
                              color={''}
                              placement="top"
                              title={''}
                            >
                              <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                  width: `calc(100% / 9)`,
                                  position: 'relative',
                                  fontSize: '14px',
                                  fontWeight: '400',
                                  borderLeft: isDisplayTherapyWeek(group, frequencyStartDate)
                                    ? `6px solid ${
                                        isShowFrequencyAlert(group, frequencyStartDate)
                                          ? '#E41000'
                                          : '#6750A4'
                                      }`
                                    : 'none',
                                  color: isGroupDisabled ? neutral[500] : neutral[900],
                                  cursor: isGroupDisabled ? 'not-allowed' : 'pointer',
                                  backgroundColor: isGroupDisabled
                                    ? 'rgba(56, 66, 80, 0.08)'
                                    : getCellBackgroundColor(group, index, true),
                                  pointerEvents: isGroupDisabled
                                    ? 'none'
                                    : getCellPointerEvents(group, true, true),
                                }}
                                onMouseEnter={() => {
                                  setHoveredType('group');
                                  setDateToShowIcon(group?.date);
                                }}
                                onMouseLeave={() => {
                                  setHoveredType(null);
                                  setDateToShowIcon('');
                                }}
                                onClick={(e) => {
                                  if (isGroupDisabled) return;
                                  handleEditCell(e, group, index, 'group');
                                }}
                                data-testid={planningCellTestId(therapyType, group?.date, 'group')}
                              >
                                {therapyPlanningStartDate === group?.date &&
                                  permissionData.includes(THERAPY_TEMPLATE) &&
                                  disableTheCopyPasteIcon(group) &&
                                  therapyTemplatesEnabled && (
                                    <Tooltip
                                      placement="bottom-start"
                                      title="Templates"
                                    >
                                      <IconButton
                                        sx={{
                                          position: 'absolute',
                                          padding: { md: '8px', xs: '5px' },
                                          borderRadius: '0px 4px 4px 0px',
                                          background: 'rgba(99, 102, 241, 0.50)',
                                          '&:hover': {
                                            background: 'rgba(99, 102, 241, 0.50)',
                                          },
                                          left: '0px',
                                          color: 'white',
                                          top: { xs: '14px', md: '10px' },
                                          zIndex: 10,
                                          pointerEvents: 'auto',
                                        }}
                                        onMouseEnter={() => setCopyTemplateStartDate(group?.date)}
                                        onMouseLeave={() => {
                                          setCopyTemplateStartDate(null);
                                        }}
                                        onClick={(event) => handleCopyTemplate(event, group)}
                                      >
                                        <TemplatePasteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {disablePlannerMin(
                                  group?.date,
                                  actualDischargeDate ?? dischargeDate
                                ) ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '-webkit-fill-available',
                                      justifyContent: isCellHovered('group', group?.date)
                                        ? 'normal'
                                        : 'center',
                                      gap: 0.2,
                                    }}
                                  >
                                    {renderCellIcon(group, 'group')}
                                    {editingCell?.date === group?.date &&
                                    editingCell?.type === 'group'
                                      ? renderEditableInput(
                                          group,
                                          'list3',
                                          inputRefs3,
                                          'group',
                                          index
                                        )
                                      : checkIsDosLess(group, dosOfTherapyCard) ||
                                        isBlankPastDay100(group)
                                      ? null
                                      : renderCellMinutes(group, 'group', true)}
                                  </Box>
                                ) : null}
                              </Stack>
                            </LightTooltip>
                          );
                        })}
                      </Stack>
                    )}
                  </>
                ) : null}

                {therapyPlanning_2_0?.loading && (
                  <Stack
                    sx={{ padding: '.5rem' }}
                    gap="1.2rem"
                  >
                    <Skeleton
                      height={25}
                      variant="rounded"
                    />
                    <Skeleton
                      variant="rounded"
                      height={25}
                      animation="wave"
                    />
                    <Skeleton
                      variant="rounded"
                      height={25}
                    />
                  </Stack>
                )}
                {isSummaryCardLoading(
                  therapyPlanningSummary?.loading,
                  therapyPlanningSummary?.requestDiscipline,
                  therapyType
                ) ? (
                  <Stack
                    sx={{ padding: '.5rem' }}
                    gap="1.2rem"
                  >
                    <Skeleton
                      variant="rounded"
                      height={25}
                    />
                  </Stack>
                ) : (
                  <Stack
                    flexDirection="row"
                    sx={{
                      flex: 1,
                      background: '#F8F9FA',
                    }}
                  >
                    {summaryData?.map((summary: any, index: any) => (
                      <LightTooltip
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, 48],
                                },
                              },
                            ],
                          },
                        }}
                        backgroundColor={''}
                        color={''}
                        placement="top"
                        title=""
                        key={index}
                      >
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            width: `calc(100% / 9)`,
                            fontSize: '14px',
                            position: 'relative',
                            fontWeight: '400',

                            color: summary?.summary >= 25 ? '#E07B00' : neutral[900],
                          }}
                        >
                          {disablePlanerSummaryByDischarge(
                            summary?.date,
                            actualDischargeDate ?? dischargeDate
                          ) ||
                          checkIsDosLessSummary(
                            summary?.date,
                            dosOfTherapyCard
                          ) ? null : summary?.summary ? (
                            <> {summary?.summary}%</>
                          ) : null}
                        </Stack>
                      </LightTooltip>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Card>
      <BootstrapDialog
        onClose={handleTemplateClose}
        aria-labelledby="customized-dialog-title"
        open={openTemplate}
      >
        <TherapyPlanningTemplate_2_0
          close={handleTemplateClose}
          therapyType={therapyType}
          getTherapyTemplateCell={getTherapyTemplateCell}
          dischargeDate={actualDischargeDate ?? dischargeDate}
        />
      </BootstrapDialog>

      <BootstrapDialog
        onClose={handleTemplateCloseApply}
        aria-labelledby="customized-dialog-title"
        open={applyTemplate}
      >
        <TherapyPlanningTemplateApply
          close={handleTemplateCloseApply}
          therapyType={therapyType}
          getTherapyTemplateCell={getTherapyTemplateCell}
          dischargeDate={actualDischargeDate ?? dischargeDate}
        />
      </BootstrapDialog>

      <TherapyPlanningTemplateMenu
        anchorEl={anchorEl}
        handleCloseTemplateMenu={handleCloseTemplateMenu}
        open={open}
        setOpenTemplate={setOpenTemplate}
        setApplyTemplate={setApplyTemplate}
        setAnchorEl={setAnchorEl}
      />
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={handleCloseInfoModal}
        aria-labelledby="customized-dialog-title"
        open={deleteModalInfo.open || false}
      >
        <DeletePopupModal
          close={handleCloseInfoModal}
          message={deleteModalInfo.message}
          button={{ saveLabel: deleteModalInfo.saveLabel, closeLabel: deleteModalInfo.closeLabel }}
          title={deleteModalInfo.title}
          handleSaveBtn={handleSaveSplitMin}
          errorType={deleteModalInfo?.errorType}
          testIds={confirmCancelTestIds('split-remove')}
        />
      </BootstrapDialog>

      <WarningModal
        open={overlapModalOpen}
        title="Overlapping Appointment"
        message="This change will create an overlap with another appointment for this patient. Continue?"
        buttonText="Confirm"
        cancelBtn="Cancel"
        primaryCTA
        onClose={handleOverlapCancel}
        handleSaveBtn={handleOverlapConfirm}
        testIds={confirmCancelTestIds('overlap')}
      />
    </>
  );
};

export default React.memo(TherapyCard_2_0);
